import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router";
import {
  PennButton,
  PennCheckbox,
  PennDropdown,
  PennInput,
  PennModal,
  PennText,
} from "@/components/ui";
import { useAppSelector } from "@/stores/hooks";
import {
  useAllAssignedTasks,
  useAllMyTasks,
  useAllTasksQueue,
} from "@/hooks/useTasks";
import type { ListTasksQuery, TaskSummary } from "@/api/tasks";
import { machineLabel } from "@/utils/machine-label";
import { SupportResponseModal } from "./SupportResponseModal";

type StatusFilter = "open" | "all" | "quoted";

type TasksModalProps = {
  open: boolean;
  onClose: () => void;
  view?: "tasks" | "mine";
};

const statusFilterOptions = [
  { value: "open", label: "Open tasks" },
  { value: "all", label: "All tasks" },
  { value: "quoted", label: "Quoted tasks" },
];

const statusColumnOptions = [
  { value: "", label: "Any status" },
  { value: "open", label: "Open" },
  { value: "quoted", label: "Quoted" },
];

/** Normalize a task's lifecycle status for filtering and display.
 * CR-005: message-only support responses stamp the task Quoted (no bespoke
 * "closed" status), so lifecycle is just open vs quoted. */
function deriveStatus(t: TaskSummary): "open" | "quoted" {
  if (t.quotedAt || (t.status ?? "").toLowerCase() === "quoted") {
    return "quoted";
  }
  return "open";
}

/** A workflow step counts as "done" across the synonyms svc-07 may emit.
 * Same set as utils/task-pending.ts so the chips and the header red dot agree
 * (PENN-075). */
function isWorkflowDone(status: string | null | undefined): boolean {
  const v = (status ?? "").toLowerCase();
  return (
    v === "done" ||
    v === "approved" ||
    v === "complete" ||
    v === "completed" ||
    v === "quoted"
  );
}

/** Engineering rejected the task: svc-07 stamps
 *  `tasks.workflow_eng_review_status = 'rejected'` on an engineer-flow reject
 *  (approvals.service), so the list can render the rejection without a live
 *  svc-07 call. (PENN-095 #3.) */
function isEngRejected(status: string | null | undefined): boolean {
  return (status ?? "").toLowerCase() === "rejected";
}

type ChipTone = "green" | "amber" | "red" | "slate";

const CHIP_TONE: Record<ChipTone, string> = {
  green: "bg-emerald-100 text-emerald-800",
  amber: "bg-amber-100 text-amber-800",
  red: "bg-red-100 text-red-800",
  slate: "bg-slate-100 text-slate-600",
};

const WorkflowBadge = ({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone: ChipTone;
}) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium whitespace-nowrap ${CHIP_TONE[tone]}`}
  >
    {label}: {value}
  </span>
);

/** POC-parity dual workflow chips: Engineering Review + Quoted, side by side.
 * Sales-flow tasks have no engineering review step (bug-061 / R-4): Quoted only.
 * PENN-095 #3: when Engineering REJECTS the task the review chip turns red
 * ("Rejected") and the Quote chip reads "Not applicable" — a rejected review can
 * never be quoted, so "Pending" would misrepresent the state. Sales-flow tasks
 * have no eng-review column, so sales rejection isn't shown here (the RFQ side
 * panel carries the full approval state). */
const WorkflowChips = ({ task }: { task: TaskSummary }) => {
  // CR-005: a responded message-only support request is stamped Quoted; show a
  // single "Support: Quoted" chip rather than the eng-review/quote pair.
  if (!task.hasSnapshot && task.quotedAt) {
    return (
      <span className="flex flex-col gap-1 items-start">
        <WorkflowBadge label="Support" value="Quoted" tone="green" />
      </span>
    );
  }
  const isSalesFlow = task.flowType === "sales";
  const engRejected =
    !isSalesFlow && isEngRejected(task.workflowEngReviewStatus);
  const engDone = isWorkflowDone(task.workflowEngReviewStatus);
  const quoteDone =
    isWorkflowDone(task.workflowQuoteStatus) ||
    !!task.quotedAt ||
    task.status === "quoted";
  return (
    <span className="flex flex-col gap-1 items-start">
      {!isSalesFlow && (
        <WorkflowBadge
          label="Engineering Review"
          value={engRejected ? "Rejected" : engDone ? "Done" : "Pending"}
          tone={engRejected ? "red" : engDone ? "green" : "amber"}
        />
      )}
      <WorkflowBadge
        label="Quoted"
        value={engRejected ? "Not applicable" : quoteDone ? "Done" : "Pending"}
        tone={engRejected ? "slate" : quoteDone ? "green" : "amber"}
      />
    </span>
  );
};

function formatDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Date + time rendered in the viewer's local timezone (the browser default),
// so a user always sees when a task was created in their own clock.
function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function requesterLabel(t: TaskSummary): string {
  // POC parity: show the USER NAME (not the company). Prefer the customer being
  // worked-for so a staff "Work-For" request is attributed to the customer, then
  // the requester's own name; fall back to email / "You".
  const workingForName = [t.workingForFirstName, t.workingForLastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  const requesterName = [t.requestedByFirstName, t.requestedByLastName]
    .filter(Boolean)
    .join(" ")
    .trim();
  return (
    workingForName ||
    requesterName ||
    t.workingForEmail ||
    t.requestedByEmail ||
    "You"
  );
}

/** Customer email for the "requested by" sub-line (POC shows email under name). */
function requesterEmail(t: TaskSummary): string | null {
  return t.workingForEmail || t.requestedByEmail || null;
}

export const TasksModal = ({
  open,
  onClose,
  view = "tasks",
}: TasksModalProps) => {
  const navigate = useNavigate();
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("open");
  const [myTasksOnly, setMyTasksOnly] = useState(true);
  const [search, setSearch] = useState("");
  const [colFilters, setColFilters] = useState({
    date: "",
    requester: "",
    machineType: "",
    status: "",
  });
  // CR-005: message-only support task opened in the Respond & Close popup.
  const [supportTaskId, setSupportTaskId] = useState<string | null>(null);

  // ADR-0027/0028 keep three distinct views: current responsibility
  // assignments, the broader staff queue, and target-owned personal RFQ
  // history (`/mine`).
  const isInternal = useAppSelector(
    (s) => !!s.auth.user?.role?.canWorkForOthers,
  );
  const showPersonal = !isInternal || view === "mine";
  const showQueue = isInternal && !showPersonal && !myTasksOnly;

  useEffect(() => {
    if (!open) {
      setMyTasksOnly(true);
      setStatusFilter("open");
      return;
    }
    if (open && view === "tasks") {
      setMyTasksOnly(true);
      setStatusFilter("open");
    }
  }, [open, view]);

  const serverQuery = useMemo<ListTasksQuery>(() => {
    if (statusFilter === "open") return { quoted: "false" };
    if (statusFilter === "quoted") return { quoted: "true" };
    return {};
  }, [statusFilter]);

  // Issue #8: show the FULL task/RFQ list — fetch every row (no page cap) by
  // paging through the endpoint until exhausted.
  const assignedQuery = useAllAssignedTasks(
    serverQuery,
    open && isInternal && !showPersonal && myTasksOnly,
  );
  const mineQuery = useAllMyTasks(serverQuery, open && showPersonal);
  const queueQuery = useAllTasksQueue(serverQuery, open && showQueue);
  const activeQuery = showPersonal
    ? mineQuery
    : showQueue
      ? queueQuery
      : assignedQuery;
  const { data, isLoading, isError, refetch, isFetching } = activeQuery;

  const items = useMemo(() => data?.items ?? [], [data]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return items.filter((t) => {
      const status = deriveStatus(t);
      if (statusFilter !== "all" && status !== statusFilter) return false;
      const requester = requesterLabel(t).toLowerCase();
      const note = (t.comment ?? "").toLowerCase();
      const machineType = machineLabel(t).toLowerCase();
      if (
        q &&
        !requester.includes(q) &&
        !note.includes(q) &&
        !machineType.includes(q)
      ) {
        return false;
      }
      if (
        colFilters.requester &&
        !requester.includes(colFilters.requester.toLowerCase())
      ) {
        return false;
      }
      if (
        colFilters.date &&
        !formatDate(t.createdAt)
          .toLowerCase()
          .includes(colFilters.date.toLowerCase())
      ) {
        return false;
      }
      if (
        colFilters.machineType &&
        !machineType.includes(colFilters.machineType.toLowerCase())
      ) {
        return false;
      }
      if (colFilters.status && status !== colFilters.status) return false;
      return true;
    });
  }, [items, search, statusFilter, colFilters]);

  return (
    <PennModal
      open={open}
      onClose={onClose}
      title={showPersonal ? "My RFQs / Quotes" : "Tasks"}
      subtitle={
        showPersonal
          ? "Requests submitted by or prepared for the active customer, and their current status."
          : "Quotation/support requests currently routed to your responsibility."
      }
      maxWidth="max-w-[1320px]"
      zIndex="z-[115]"
    >
      {/* Filters */}
      <div className="px-6 py-4 border-b border-gray-100 space-y-3">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          <div className="flex items-center gap-2">
            <PennText as="label" size="sm" color="slate-900" font="medium">
              Show
            </PennText>
            <div className="w-44">
              <PennDropdown
                options={statusFilterOptions}
                value={statusFilter}
                onChange={(v) => setStatusFilter(v as StatusFilter)}
              />
            </div>
          </div>

          {isInternal && !showPersonal && (
            <label className="inline-flex items-center gap-2 cursor-pointer">
              <PennCheckbox
                checked={myTasksOnly}
                onChange={(e) => setMyTasksOnly(e.target.checked)}
              />
              <PennText as="span" size="sm" color="slate-900" font="medium">
                My tasks only
              </PennText>
            </label>
          )}

          <div className="flex-1" />

          <PennInput
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tasks (name, email, machine type)"
            className="flex-1 min-w-[220px]"
          />

          <PennButton
            variant="secondary"
            size="md"
            onClick={() => refetch()}
            disabled={isFetching}
          >
            {isFetching ? "Refreshing…" : "Refresh"}
          </PennButton>
        </div>

        {/* Column filters */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
          <PennInput
            value={colFilters.date}
            onChange={(e) =>
              setColFilters((p) => ({ ...p, date: e.target.value }))
            }
            placeholder="Filter date"
          />
          <PennInput
            value={colFilters.requester}
            onChange={(e) =>
              setColFilters((p) => ({ ...p, requester: e.target.value }))
            }
            placeholder="Filter requested by"
          />
          <PennInput
            value={colFilters.machineType}
            onChange={(e) =>
              setColFilters((p) => ({ ...p, machineType: e.target.value }))
            }
            placeholder="Filter machine type"
          />
          <PennDropdown
            options={statusColumnOptions}
            value={colFilters.status}
            onChange={(v) => setColFilters((p) => ({ ...p, status: v }))}
          />
        </div>
      </div>

      {/* Task list */}
      <div className="px-6 py-4">
        {isLoading ? (
          <div className="py-10 text-center">
            <PennText as="p" size="sm" color="slate-500">
              Loading your requests…
            </PennText>
          </div>
        ) : isError ? (
          <div className="py-10 text-center border border-dashed border-red-200 rounded-2xl">
            <PennText as="p" size="sm" color="slate-500">
              Could not load your requests. Please try Refresh.
            </PennText>
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-10 text-center border border-dashed border-gray-200 rounded-2xl">
            <PennText as="p" size="sm" color="slate-500">
              No requests found for the selected filters.
            </PennText>
          </div>
        ) : (
          <div className="border border-gray-200 rounded-2xl max-h-[55vh] overflow-auto penn-scrollbar">
            <div className="min-w-[640px] divide-y divide-gray-100">
              {/* POC parity: Date · Requested by · Machine type · Status · Actions.
                The comment/note has no dedicated column — it renders as a
                truncated line under Machine type with a full-text hover title. */}
              <div className="grid grid-cols-[1fr_1.4fr_1fr_1.2fr_auto] gap-4 px-5 py-2 bg-gray-50 sticky top-0">
                {[
                  "Date",
                  "Requested by",
                  "Machine type / Comment",
                  "Status",
                  "Action",
                ].map((h) => (
                  <PennText
                    key={h}
                    as="span"
                    size="xs"
                    color="slate-500"
                    font="semibold"
                    className="uppercase"
                  >
                    {h}
                  </PennText>
                ))}
              </div>
              {filtered.map((task) => (
                <TaskRow
                  key={task.taskId}
                  task={task}
                  onView={() => {
                    // CR-005: a message-only support request (no machine, no
                    // applications) opens the Respond & Close popup inline rather
                    // than loading the (empty) selector, which previously just
                    // scrolled to the application-parameters section.
                    if (!task.hasSnapshot && task.comment?.trim()) {
                      setSupportTaskId(task.taskId);
                      return;
                    }
                    // Single "View" (POC openTaskFromList) — load the task straight
                    // into the selector + Configure. The selector lives on FilterPage
                    // (this modal is mounted in the NavBar), so hand off the id via
                    // router state and close the modal; FilterPage rehydrates from the
                    // frozen snapshot and opens Configure with the RFQ side panel.
                    navigate("/installation-system", {
                      state: { openTaskId: task.taskId },
                    });
                    onClose();
                  }}
                />
              ))}
            </div>
          </div>
        )}
      </div>
      {supportTaskId && (
        <SupportResponseModal
          taskId={supportTaskId}
          onClose={() => setSupportTaskId(null)}
        />
      )}
    </PennModal>
  );
};

/** Hover tooltip + click-to-pin popover for the comment cell, matching the
 * Prices-cell tooltip on Dataset: Profil InDie Options (price col) so both
 * admin tables behave the same way. */
const CommentCell = ({ comment }: { comment: string }) => {
  const [hovering, setHovering] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(
    null,
  );
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const popRef = useRef<HTMLDivElement | null>(null);

  const reposition = () => {
    const el = triggerRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const POP_W = 260;
    const POP_H = 120;
    const margin = 8;
    let left = r.left;
    let top = r.bottom + 6;
    if (left + POP_W + margin > window.innerWidth)
      left = window.innerWidth - POP_W - margin;
    if (left < margin) left = margin;
    if (top + POP_H + margin > window.innerHeight) top = r.top - POP_H - 6;
    if (top < margin) top = margin;
    setCoords({ top, left });
  };

  useEffect(() => {
    if (!pinned && !hovering) return;
    reposition();
    const onScrollOrResize = () => reposition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [pinned, hovering]);

  useEffect(() => {
    if (!pinned) return;
    const onDocClick = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || triggerRef.current?.contains(t))
        return;
      setPinned(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [pinned]);

  const open = pinned || hovering;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        onMouseEnter={() => setHovering(true)}
        onMouseLeave={() => setHovering(false)}
        onClick={(e) => {
          e.stopPropagation();
          setPinned((p) => !p);
        }}
        className="block max-w-full truncate text-left text-xs italic text-slate-500 cursor-help"
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        {comment}
      </button>

      {open &&
        coords &&
        createPortal(
          <div
            ref={popRef}
            role="dialog"
            onMouseEnter={() => pinned || setHovering(true)}
            onMouseLeave={() => setHovering(false)}
            style={{
              position: "fixed",
              top: coords.top,
              left: coords.left,
              width: 260,
              zIndex: 9999,
            }}
            className="rounded-lg border border-gray-200 bg-white shadow-lg p-3 text-xs text-gray-800 whitespace-pre-wrap break-words"
          >
            {comment}
          </div>,
          document.body,
        )}
    </>
  );
};

const TaskRow = ({
  task,
  onView,
}: {
  task: TaskSummary;
  onView: () => void;
}) => {
  return (
    <div className="grid grid-cols-[1fr_1.4fr_1fr_1.2fr_auto] gap-4 px-5 py-3 hover:bg-gray-50 items-center">
      <PennText as="span" size="sm" color="slate-500">
        {formatDateTime(task.createdAt)}
      </PennText>
      <div className="min-w-0" title={requesterLabel(task)}>
        <PennText as="p" size="sm" color="slate-900" className="truncate">
          {requesterLabel(task)}
        </PennText>
        {/* POC parity: requester email under the name so the right task is
            identifiable (esp. Work-For). Hidden when it duplicates the name. */}
        {(() => {
          const email = requesterEmail(task);
          return email && email !== requesterLabel(task) ? (
            <PennText as="p" size="xs" color="slate-500" className="truncate">
              {email}
            </PennText>
          ) : null;
        })()}
      </div>
      <div className="min-w-0" title={machineLabel(task)}>
        <PennText as="p" size="sm" color="slate-500" className="truncate">
          {machineLabel(task)}
        </PennText>
        {task.comment?.trim() ? (
          <CommentCell comment={task.comment} />
        ) : (
          <PennText as="p" size="xs" color="slate-500" className="italic">
            N/A
          </PennText>
        )}
      </div>
      <WorkflowChips task={task} />
      <span className="flex items-center justify-end">
        <PennButton variant="primary" size="sm" onClick={onView}>
          View
        </PennButton>
      </span>
    </div>
  );
};
